import apolloClient from '@/lib/apollo-client';
import { UPDATE_PROFILE_MUTATION, UPDATE_PROFILE_PHOTO_MUTATION } from '@/lib/graphql/mutations';
import { GET_PROFILE, GET_PROFILE_PIC } from '@/lib/graphql/queries';
import { getCookie } from '@/utils/cookies';
import { env } from '@/utils/env';

export interface Profile {
    id: string;  // Make sure id is required
    email: string;
    roles: string[];
    username: string;
    bio?: string | null;
    location?: string | null;
    skills?: string[] | null;
    title?: string | null;
    linkedin?: string | null;
    github?: string | null;
    website?: string | null;
    x?: string | null;
    profilePhoto?: string | null;
    coverPhoto?: string | null;
    mobile: string | null;
    
    experiences?: Experience[] | null;
    certifications?: Certification[] | null;
    education?: Education[] | null;
}

// Add these new interfaces for the nested objects
export interface Experience {
    title: string;
    company: string;
    duration: string;
    description?: string | null;
    accomplishments?: string[] | null;
}

export interface Certification {
    name: string;
    url?: string | null;
}

export interface Education {
    institution: string;
    degree: string;
    fieldOfStudy: string;
    startDate: string;
    endDate: string;
    description?: string | null;
}

export interface ProfileUpdateInput {
    id?: string;      // Add id field
    username?: string;
    bio?: string;
    location?: string;
    title?: string;
    skills?: string[];
    linkedin?: string;
    github?: string;
    x?: string;
    website?: string;
}

interface ProfilePicture {
  base64Data: string;
  mimetype: string;
}

export const getProfile = async (): Promise<Profile> => {
    const token = getCookie('token');
    try {
        const { data } = await apolloClient.query({
            query: GET_PROFILE,
            context: {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            },
            fetchPolicy: 'network-only' // Don't use cache for profile
        });

        return data.getProfile;
    } catch (error) {
        console.error('Error fetching profile:', error);
        throw error;
    }
};

// Update the updateProfile function
export const updateProfile = async (input: ProfileUpdateInput): Promise<Profile> => {
  const token = getCookie('token');
  try {
    const { data } = await apolloClient.mutate({
      mutation: UPDATE_PROFILE_MUTATION,
      variables: { 
        input: {
          ...input,
          id: input.id || undefined // Ensure id is passed if available
        } 
      },
      context: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    });

    if (!data.updateProfile) {
      throw new Error('Failed to update profile');
    }

    return data.updateProfile;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

export const updateProfilePhoto = async (file: File): Promise<Profile> => {
    const token = getCookie('token');
    try {
        // Create FormData
        const formData = new FormData();
        formData.append('operations', JSON.stringify({
            query: `
                mutation UpdateProfilePhoto($profilePhoto: Upload!) {
                    updateProfilepic(profilePhoto: $profilePhoto) {
                        id
                        email
                        username
                        profilePhoto
                        roles
                    }
                }
            `,
            variables: {
                profilePhoto: null
            },
            operationName: "UpdateProfilePhoto"
        }));
        
        // Map the file to the correct variable
        formData.append('map', JSON.stringify({
            "0": ["variables.profilePhoto"]
        }));

        // Append the actual file with the correct key
        formData.append('0', file, file.name);

        const response = await fetch(env.graphqlUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'apollo-require-preflight': 'true',
                'x-apollo-operation-name': 'UpdateProfilePhoto'
            },
            body: formData
        });

        const { data, errors } = await response.json();
        if (errors) {
            throw new Error(errors[0].message);
        }
        return data.updateProfilepic;
    } catch (error) {
        console.error('Error updating profile photo:', error);
        throw error;
    }
};

export const updateCoverPhoto = async (file: File): Promise<Profile> => {
    const token = getCookie('token');
    try {
        // Create FormData
        const formData = new FormData();
        formData.append('operations', JSON.stringify({
            query: `
                mutation UpdateCoverPhoto($coverPhoto: Upload!) {
                    updateCoverPic(coverPhoto: $coverPhoto) {
                        id
                        email
                        username
                        coverPhoto
                        roles
                    }
                }
            `,
            variables: {
                coverPhoto: null
            },
            operationName: "UpdateCoverPhoto"
        }));
        
        // Map the file to the correct variable
        formData.append('map', JSON.stringify({
            "0": ["variables.coverPhoto"]
        }));

        // Append the actual file with the correct key
        formData.append('0', file, file.name);

        const response = await fetch(env.graphqlUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'apollo-require-preflight': 'true',
                'x-apollo-operation-name': 'UpdateCoverPhoto'
            },
            body: formData
        });

        const { data, errors } = await response.json();
        if (errors) {
            throw new Error(errors[0].message);
        }
        return data.updateCoverPic;
    } catch (error) {
        console.error('Error updating cover photo:', error);
        throw error;
    }
};

export const getProfilePicture = async (filename: string): Promise<ProfilePicture> => {
    const token = getCookie('token');
    try {
        const { data } = await apolloClient.query({
            query: GET_PROFILE_PIC,
            variables: { filename },
            fetchPolicy: 'no-cache' // Don't cache profile pictures
        });

        return data.getProfilePicByName;
    } catch (error) {
        console.error('Error fetching profile picture:', error);
        throw error;
    }
};

